<template>
  <div class="mt-4">
    <!-- 상단 -->
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense>
        <v-btn icon dark @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>단타의 신 · 매체사 콜백 모니터링</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-switch v-model="autoRefresh" dense hide-details color="cyan" class="mr-3" label="30초 자동 새로고침"></v-switch>
        <v-btn color="cyan" dark small @click="refreshAll">
          <v-icon left small>mdi-refresh</v-icon>새로고침
        </v-btn>
      </v-toolbar>
    </v-card>

    <!-- 요약 카드 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-card-title class="py-2">
        <v-icon left small>mdi-chart-box-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">Worker 상태</span>
        <v-spacer></v-spacer>
        <v-chip v-if="workerStale" small color="red" dark>worker stale (60s+)</v-chip>
        <v-chip v-else-if="stats.worker" small color="green" dark>
          updated {{ workerAgeSec }}s ago
        </v-chip>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row dense>
          <v-col cols="6" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-2">
                <div class="text-caption grey--text">처리 대기 (ready)</div>
                <div class="text-h5 font-weight-bold" :class="readyColor">{{ statsValue('ready') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-2">
                <div class="text-caption grey--text">진행 중 (leased)</div>
                <div class="text-h5 font-weight-bold blue--text">{{ statsValue('leased') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-2">
                <div class="text-caption grey--text">최근 1h 성공률</div>
                <div class="text-h5 font-weight-bold" :class="successRateColor">{{ successRate1h }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" md="3">
            <v-card outlined>
              <v-card-text class="text-center pa-2">
                <div class="text-caption grey--text">영구 실패</div>
                <div class="text-h5 font-weight-bold red--text">{{ statsValue('failed_total') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Circuit OPEN URL -->
    <v-card v-if="circuit.length > 0" class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-card-title class="py-2">
        <v-icon left small color="red">mdi-electric-switch-closed</v-icon>
        <span class="text-subtitle-1 font-weight-bold red--text">Circuit OPEN URL</span>
        <v-chip small color="red" dark class="ml-2">{{ circuit.length }}</v-chip>
        <v-spacer></v-spacer>
        <span class="text-caption grey--text">5회 연속 실패 → 60초 자동 차단 (해제 후 자동 복구)</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="circuitHeaders"
          :items="circuit"
          :items-per-page="5"
          hide-default-footer
          class="elevation-0"
          dense
        >
          <template v-slot:item.remaining="{ item }">
            <span v-if="item.open" class="red--text font-weight-bold">{{ Math.ceil(item.remainingMs / 1000) }}s</span>
            <span v-else class="grey--text">해제됨</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- 매체사별 24h 성공률 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-card-title class="py-2">
        <v-icon left small>mdi-account-supervisor-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">매체사별 24h 성공률</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="mediaHeaders"
          :items="stats.media || []"
          :items-per-page="10"
          :footer-props="{'items-per-page-options':[10, 20, -1]}"
          class="elevation-0"
          dense
        >
          <template v-slot:item.success_rate="{ item }">
            <v-chip v-if="item.success_rate === null" small outlined>—</v-chip>
            <v-chip v-else small :color="rateColor(item.success_rate)" dark>{{ item.success_rate }}%</v-chip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- 콜백 로그 리스트 -->
    <v-card class="mx-auto mt-4 mb-8" max-width="95%" elevation="2">
      <v-card-title class="py-2">
        <v-icon left small>mdi-format-list-bulleted</v-icon>
        <span class="text-subtitle-1 font-weight-bold">콜백 로그 (최근 7일)</span>
        <v-chip v-if="list.total > 0" small outlined class="ml-2">{{ list.total }}</v-chip>
        <v-spacer></v-spacer>
        <v-btn
          v-if="selected.length > 0"
          color="orange"
          dark
          small
          class="mr-2"
          @click="retrySelected"
          :loading="retryLoading"
        >
          <v-icon left small>mdi-refresh-circle</v-icon>선택한 {{ selected.length }}건 재발송
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-2">
        <v-row dense>
          <v-col cols="6" md="3">
            <v-select
              v-model="filter.status"
              :items="statusOptions"
              label="상태"
              dense outlined hide-details
              clearable
              @change="loadList"
            ></v-select>
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="filter.event_type"
              :items="eventOptions"
              label="이벤트"
              dense outlined hide-details
              clearable
              @change="loadList"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="filter.app_key"
              label="app_key"
              dense outlined hide-details
              clearable
              @keyup.enter="loadList"
              @click:clear="loadList"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
      <v-data-table
        v-model="selected"
        :headers="logHeaders"
        :items="list.rows"
        :items-per-page="list.page_size"
        :server-items-length="list.total"
        :page.sync="list.page"
        :footer-props="{'items-per-page-options':[20, 50, 100, 500]}"
        item-key="id"
        show-select
        :single-select="false"
        @update:page="loadList"
        @update:items-per-page="(v) => { list.page_size = v; loadList(); }"
        class="elevation-0"
        dense
      >
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>{{ item.status }}</v-chip>
        </template>
        <template v-slot:item.retry_count="{ item }">
          <span :class="item.retry_count >= item.max_retries ? 'red--text font-weight-bold' : ''">
            {{ item.retry_count }}/{{ item.max_retries }}
          </span>
        </template>
        <template v-slot:item.callback_url="{ item }">
          <span class="text-caption" :title="item.callback_url">{{ shortUrl(item.callback_url) }}</span>
        </template>
        <template v-slot:item.last_error="{ item }">
          <span class="text-caption red--text" :title="item.last_error || item.error_message">
            {{ shortText(item.last_error || item.error_message, 40) }}
          </span>
        </template>
        <template v-slot:item.created_at="{ item }">
          <span class="text-caption">{{ formatDate(item.created_at) }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.status === 'failed'"
            x-small color="orange" dark
            @click="retryOne(item.id)"
          >
            <v-icon left x-small>mdi-refresh</v-icon>재발송
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- 스낵바 -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'CallbackMonitor',
  data() {
    return {
      autoRefresh: false,
      autoRefreshTimer: null,

      stats: { worker: null, media: [] },
      circuit: [],
      list: { rows: [], total: 0, page: 1, page_size: 50 },
      selected: [],

      filter: { status: null, event_type: null, app_key: '' },
      retryLoading: false,

      statusOptions: [
        { text: '전체', value: null },
        { text: 'pending', value: 'pending' },
        { text: 'success', value: 'success' },
        { text: 'failed',  value: 'failed' },
      ],
      eventOptions: [
        { text: '전체', value: null },
        { text: 'round_started',  value: 'round_started' },
        { text: 'round_closed',   value: 'round_closed' },
        { text: 'round_ended',    value: 'round_ended' },
        { text: 'round_canceled', value: 'round_canceled' },
        { text: 'bet_request',    value: 'bet_request' },
        { text: 'bet_confirmed',  value: 'bet_confirmed' },
        { text: 'bet_canceled',   value: 'bet_canceled' },
      ],

      circuitHeaders: [
        { text: 'URL', value: 'url' },
        { text: '실패 횟수', value: 'failureCount', width: '120px', align: 'center' },
        { text: 'OPEN 시각', value: 'openedAt', width: '200px' },
        { text: '남은 차단', value: 'remaining', width: '120px', align: 'center' },
      ],
      mediaHeaders: [
        { text: '매체사', value: 'media_name' },
        { text: 'app_key', value: 'app_key' },
        { text: '총 (24h)', value: 'total_24h', align: 'end' },
        { text: '성공', value: 'success_24h', align: 'end' },
        { text: '실패', value: 'failed_24h', align: 'end' },
        { text: '대기', value: 'pending_24h', align: 'end' },
        { text: '성공률', value: 'success_rate', align: 'center' },
      ],
      logHeaders: [
        { text: 'ID', value: 'id', width: '90px' },
        { text: '이벤트', value: 'event_type', width: '150px' },
        { text: 'URL', value: 'callback_url' },
        { text: '상태', value: 'status', width: '90px', align: 'center' },
        { text: 'retry', value: 'retry_count', width: '80px', align: 'center' },
        { text: 'HTTP', value: 'http_status', width: '70px', align: 'center' },
        { text: '에러', value: 'last_error' },
        { text: '생성', value: 'created_at', width: '160px' },
        { text: '액션', value: 'actions', width: '110px', sortable: false, align: 'center' },
      ],

      snackbar: { show: false, color: 'info', text: '' },
    };
  },
  computed: {
    workerAgeSec() {
      if (!this.stats.worker || !this.stats.worker.updatedAt) return null;
      return Math.round((Date.now() - new Date(this.stats.worker.updatedAt).getTime()) / 1000);
    },
    workerStale() {
      return this.workerAgeSec !== null && this.workerAgeSec > 60;
    },
    readyColor() {
      const v = Number(this.stats.worker?.ready || 0);
      if (v > 100) return 'red--text';
      if (v > 20) return 'orange--text';
      return 'green--text';
    },
    successRate1h() {
      const w = this.stats.worker;
      if (!w || !w.total_1h) return '—';
      const r = (w.success_1h / w.total_1h) * 100;
      return r.toFixed(1) + '%';
    },
    successRateColor() {
      const w = this.stats.worker;
      if (!w || !w.total_1h) return 'grey--text';
      const r = (w.success_1h / w.total_1h);
      if (r >= 0.95) return 'green--text';
      if (r >= 0.80) return 'orange--text';
      return 'red--text';
    },
  },
  methods: {
    statsValue(key) {
      return this.stats.worker ? Number(this.stats.worker[key] || 0) : '—';
    },
    rateColor(rate) {
      if (rate >= 95) return 'green';
      if (rate >= 80) return 'orange';
      return 'red';
    },
    statusColor(s) {
      return s === 'success' ? 'green' : s === 'pending' ? 'blue' : s === 'failed' ? 'red' : 'grey';
    },
    shortUrl(url) {
      if (!url) return '';
      try {
        const u = new URL(url);
        return u.host + u.pathname;
      } catch (_) {
        return url.length > 60 ? url.slice(0, 60) + '...' : url;
      }
    },
    shortText(text, max) {
      if (!text) return '';
      return text.length > max ? text.slice(0, max) + '...' : text;
    },
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d);
      return dt.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', hour12: false });
    },

    async refreshAll() {
      await Promise.all([this.loadStats(), this.loadCircuit(), this.loadList()]);
    },
    async loadStats() {
      try {
        // request() 가 이미 response_data 를 unwrap 해서 리턴 (Game_Manage 패턴과 동일).
        // 응답 형태: { worker, media, fetched_at }
        const r = await this.$store.dispatch('DANTA_CALLBACK_STATS', {});
        if (r) this.stats = { worker: r.worker || null, media: r.media || [] };
      } catch (e) {
        this.toast('통계 조회 실패: ' + e.message, 'red');
      }
    },
    async loadCircuit() {
      try {
        // 응답 형태: { state, count, openCount }
        const r = await this.$store.dispatch('DANTA_CALLBACK_CIRCUIT', {});
        this.circuit = (r && r.state) || [];
      } catch (e) {
        this.circuit = [];
      }
    },
    async loadList() {
      try {
        // 응답 형태: { rows, total, page, page_size }
        const r = await this.$store.dispatch('DANTA_CALLBACK_LIST', {
          status: this.filter.status,
          event_type: this.filter.event_type,
          app_key: this.filter.app_key || null,
          page: this.list.page,
          page_size: this.list.page_size,
        });
        if (r) {
          this.list.rows = r.rows || [];
          this.list.total = r.total || 0;
        }
      } catch (e) {
        this.toast('리스트 조회 실패: ' + e.message, 'red');
      }
    },
    async retryOne(id) { await this._retryIds([id]); },
    async retrySelected() {
      const ids = this.selected.map((r) => r.id);
      if (ids.length === 0) return;
      await this._retryIds(ids);
      this.selected = [];
    },
    async _retryIds(ids) {
      this.retryLoading = true;
      try {
        // 응답 형태: { requested, retried, skipped, ids }
        const r = await this.$store.dispatch('DANTA_CALLBACK_RETRY', { ids });
        if (r) {
          this.toast(`${r.retried || 0}건 재발송 큐 등록 (skip ${r.skipped || 0}건)`, 'green');
          await this.loadList();
        } else {
          this.toast('재발송 실패', 'red');
        }
      } catch (e) {
        this.toast('재발송 실패: ' + e.message, 'red');
      } finally {
        this.retryLoading = false;
      }
    },

    toast(text, color = 'info') {
      this.snackbar = { show: true, text, color };
    },

    _startAutoRefresh() {
      if (this.autoRefreshTimer) clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = setInterval(() => this.refreshAll(), 30_000);
    },
    _stopAutoRefresh() {
      if (this.autoRefreshTimer) clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    },
  },
  watch: {
    autoRefresh(v) {
      if (v) this._startAutoRefresh();
      else this._stopAutoRefresh();
    },
  },
  mounted() {
    this.refreshAll();
  },
  beforeDestroy() {
    this._stopAutoRefresh();
  },
};
</script>
